import { useEffect, useId, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";

import { track } from "../lib/analytics";
import { usePublicTypeSearch } from "./api";

// FR-5 — header type-ahead. Debounces the query to 250ms (per AC-9)
// before hitting the API, renders up to 8 results inline, and links
// "See all results" to /search?q=…. Submit (Enter) navigates to the
// full results page.
const DEBOUNCE_MS = 250;
const MAX_INLINE = 8;

export function SearchDropdown(): JSX.Element {
  const inputId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [query]);

  const { data, isFetching } = usePublicTypeSearch(debounced, { enabled: open && debounced.length > 0 });

  // Fire one analytics event per settled query (debounced.length > 0
  // and not currently fetching).
  useEffect(() => {
    if (!isFetching && debounced.length > 0) {
      track({
        name: "search_performed",
        query_length: debounced.length,
        result_count: data?.length ?? 0
      });
    }
    // We only want this to fire when data settles for a given debounced
    // value, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, debounced, isFetching]);

  // Close the dropdown on outside click so it doesn't trap focus.
  useEffect(() => {
    function onClick(e: MouseEvent): void {
      if (!containerRef.current) return;
      if (e.target instanceof Node && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, []);

  const inline = (data ?? []).slice(0, MAX_INLINE);
  const overflow = (data?.length ?? 0) > MAX_INLINE;
  const showPanel = open && debounced.length > 0;

  return (
    <div ref={containerRef} className="relative">
      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          if (query.trim().length === 0) return;
          navigate(`/search?q=${encodeURIComponent(query.trim())}`);
          setOpen(false);
        }}
      >
        <label htmlFor={inputId} className="sr-only">Search equipment</label>
        <Input
          id={inputId}
          type="search"
          autoComplete="off"
          placeholder="Search equipment"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="h-10 w-64"
        />
      </form>

      {showPanel ? (
        <div
          role="listbox"
          aria-label="Search results"
          className={cn(
            "absolute left-0 right-0 top-11 z-40 max-h-80 overflow-auto",
            "rounded-md border border-zinc-200 bg-brand-white shadow-lg"
          )}
        >
          {isFetching && inline.length === 0 ? (
            <p className="px-3 py-2 text-sm text-zinc-500">Searching…</p>
          ) : null}
          {!isFetching && inline.length === 0 ? (
            <p className="px-3 py-2 text-sm text-zinc-600">
              No equipment matched &ldquo;{debounced}&rdquo;. <Link to="/" className="text-brand-red hover:underline">Browse all categories</Link>
            </p>
          ) : null}
          <ul className="divide-y divide-zinc-100">
            {inline.map((t) => (
              <li key={t.id}>
                <Link
                  to={`/c/${categorySlugFor(t)}/${t.slug}`}
                  onClick={() => setOpen(false)}
                  className="block px-3 py-2 text-sm text-brand-black hover:bg-zinc-50 focus-visible:outline-none focus-visible:bg-zinc-50"
                >
                  {t.name}
                </Link>
              </li>
            ))}
          </ul>
          {overflow ? (
            <Link
              to={`/search?q=${encodeURIComponent(debounced)}`}
              onClick={() => setOpen(false)}
              className="block border-t border-zinc-100 px-3 py-2 text-sm font-medium text-brand-red hover:bg-zinc-50"
            >
              See all results →
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// The public TypeSummary doesn't carry the category slug — the inline
// search hits /api/v1/types?query=… which renders the summary
// serializer. For the inline link we need _some_ category prefix; the
// detail page rewrites to the canonical URL on load (see
// TypeDetailPage useEffect), so a placeholder is safe.
function categorySlugFor(_t: { slug: string }): string {
  return "_";
}
